import React, { forwardRef } from "react";
import classnames from "classnames";
import PropTypes from "prop-types";

import LongPressWrapper from "../LongPressWrapper";
import Ripple from "@/shared/components/Button/Ripple";

import useOnlineOnlyHandler from "../../hooks/useOnlineOnlyHandler";

import {
  BUTTON_COLOR, BUTTON_FONT_FAMILY,
  BUTTON_MIN_WIDTH,
  BUTTON_SIZES,
  BUTTON_VARIANT,
  MapButtonFontFamilyToCN
} from "./Button.constants";
import { handleKeyDownSelectItem } from "../../utils/keyboard-navigation/keyboard-navigation";

import styles from "./Button.module.scss";

const isIconByChildren = (children) => children && (children.type === "i" || (
  typeof children.type === "function" && children.type.name?.toLowerCase().includes("icon")
) || children.props?.icon);

const getVariantFallbackBasedOnChildren = (children) => {
  if (isIconByChildren(children)) {
    return BUTTON_VARIANT.clear;
  }
  return BUTTON_VARIANT.default;
};
const getColorFallbackBasedOnChildren = (children) => {
  if (isIconByChildren(children)) {
    return BUTTON_COLOR.icon;
  }
  return BUTTON_COLOR.primary;
};
const getSizeFallbackBasedOnChildren = (children) => {
  if (isIconByChildren(children)) {
    return BUTTON_SIZES.default;
  }
  return BUTTON_SIZES.big;
};

const preventEvent = (e) => {
  e.preventDefault();
};

const Button = forwardRef(({
  idRipple,
  classNameRipple,
  rootTagName: RootTag = "button",
  className = "",
  activeClassName = "",
  variant,
  color,
  size,
  paddingLess,
  active,
  isContextMenuCustom,
  onContextMenu,
  isOnLineOnly,
  onClick,
  onKeyDown,
  timeout,
  minWidth,
  children,
  title,
  fontFamily,
  tabIndex,
  ...restProps
}, ref) => {
  const isDisabled = restProps.disabled;
  const handler = useOnlineOnlyHandler();
  const handleClick = isOnLineOnly ? handler(onClick, preventEvent) : onClick;

  const handleKeyDown = (e) => {
    if (onKeyDown) {
      onKeyDown(e);
    }

    if (!e.defaultPrevented && onClick) {
      handleKeyDownSelectItem(e, onClick);
    }
  };

  const handleLongPress = (e) => {
    e.preventDefault();
    document.dispatchEvent(new CustomEvent("contextMenuCustom", {
      detail: {
        target: e.target,
        getUrl: onContextMenu ? onContextMenu : ""
      }
    }));
  };

  const variantFinal = variant || getVariantFallbackBasedOnChildren(children);
  const colorFinal = color || getColorFallbackBasedOnChildren(children);
  const sizeFinal = size || getSizeFallbackBasedOnChildren(children);

  const rootProps = {
    ...restProps,
    onClick: handleClick,
    ["data-only-online"]: isOnLineOnly ? 1 : 0,
    onKeyDown: handleKeyDown,
    ["aria-label"]: restProps["aria-label"] || restProps.title,
    className: classnames(className,
      fontFamily ? styles[MapButtonFontFamilyToCN[fontFamily]] : undefined,
      styles["btn"], "btn",
      variantFinal, colorFinal, sizeFinal,
      styles[variantFinal], styles[colorFinal], styles[sizeFinal], styles[minWidth],
      styles["with-custom-ctx-menu"], isContextMenuCustom,
      {
        "active": active,
        [styles["active"]]: active,
        [styles["disabled"]]: isDisabled,
        [activeClassName]: active,
        [styles["padding-less"]]: paddingLess,
      }),
    tabIndex: tabIndex ?? (isDisabled ? -1 : 0),
    ref,
  };

  if (isContextMenuCustom) {
    return (
      <LongPressWrapper
        {...rootProps}
        tag={RootTag}
        timeout={timeout}
        onLongPress={handleLongPress}
        onContextMenu={handleLongPress}
      ><Ripple idRipple={idRipple} />{children}</LongPressWrapper>
    );
  }

  return <RootTag {...rootProps}><Ripple idRipple={idRipple} className={classNameRipple}/>{children}</RootTag>;
});

Button.variant = BUTTON_VARIANT;
Button.color = BUTTON_COLOR;
Button.size = BUTTON_SIZES;
Button.minWidth = BUTTON_MIN_WIDTH;
Button.fontFamily = BUTTON_FONT_FAMILY;

Button.propTypes = {
  idRipple: PropTypes.string,
  classNameRipple: PropTypes.string,
  className: PropTypes.string,
  activeClassName: PropTypes.string,
  rootTagName: PropTypes.any,
  variant: PropTypes.oneOf(Object.values(BUTTON_VARIANT)),
  color: PropTypes.oneOf(Object.values(BUTTON_COLOR)),
  size: PropTypes.oneOf(Object.values(BUTTON_SIZES)),
  minWidth: PropTypes.oneOf(Object.values(BUTTON_MIN_WIDTH)),
  fontFamily: PropTypes.oneOf(Object.values(BUTTON_FONT_FAMILY)),
  type: PropTypes.string,
  active: PropTypes.bool,
  disabled: PropTypes.bool,
  paddingLess: PropTypes.bool,
  onContextMenu: PropTypes.func,
  onClick: PropTypes.func,
  isContextMenuCustom: PropTypes.bool,
  isOnLineOnly: PropTypes.bool,
};

export default Button;
